import { timeToMinutes } from './schedulePrefs';
import { compareScheduleTime } from './scheduleTimezones';

/**
 * Hold a generated day inside the traveller's waking hours.
 *
 * The wizard asks when they wake and when they go to bed, and the prompt states
 * both as hard constraints — but the model treats them as a suggestion and
 * regularly opens a day before anyone is up. This is the part that actually
 * enforces it, after generation, where the result can be checked rather than
 * hoped for.
 */

const DAY = 24 * 60;

/** Only the shape this needs; generated slots carry a good deal more. */
interface WindowSlot {
  start?: string;
  durationMin?: number;
  /** An arrival/departure item built from a booked leg. */
  journey?: boolean;
  /** Pinned by the traveller (or by the journey builder). */
  locked?: boolean;
  [key: string]: unknown;
}

const toHHMM = (mins: number) => {
  const m = ((Math.round(mins) % DAY) + DAY) % DAY;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

/**
 * Move a day's activities inside `wakeTime`–`bedTime`, leaving the facts alone.
 *
 * A journey or locked slot is a booked or hand-pinned time: a 06:00 flight is a
 * 06:00 flight whatever the traveller said about mornings. Those keep their
 * times exactly, and still advance the cursor, so nothing is shifted on top of
 * them.
 *
 * Everything else is walked in time order behind a cursor. Each slot starts no
 * earlier than the one before it ends — which is what stops a naive "push
 * everything to 09:00" from stacking a whole morning on one minute — and is
 * pulled earlier when it would otherwise run past bedtime. When a day is
 * genuinely overfull the tail simply runs late: keeping a spot the traveller
 * asked for matters more than a tidy end time, and dropping one silently would
 * be the worst of the three.
 */
export function clampToDayWindow<T extends WindowSlot>(
  slots: T[],
  wakeTime: string,
  bedTime: string,
): T[] {
  if (!slots.length) return slots;
  // Waking hours are local. A cursor in Paris must not push New York's
  // afternoon activities into its evening merely because clocks went back.
  const groups = new Map<string, T[]>();
  for (const slot of slots) {
    const clock = slot.clock as { zone: string; date: string } | undefined;
    const key = clock ? `${clock.zone}:${clock.date}` : '';
    groups.set(key, [...(groups.get(key) || []), slot]);
  }
  if (groups.size > 1) return [...groups.values()].flatMap(group => clampToDayWindow(group, wakeTime, bedTime)).sort(compareScheduleTime);

  const wakeMin = timeToMinutes(wakeTime);
  let bedMin = timeToMinutes(bedTime);
  // A bed time at or before the wake time belongs to the following morning —
  // 09:00 to 01:00 is a sixteen-hour day, not a negative one.
  if (bedMin <= wakeMin) bedMin += DAY;

  // A start before waking is ambiguous: with a 02:00 bed time, 00:30 is
  // tonight's small hours and deliberate; with a 22:00 bed time it is the model
  // making something up. Unwrap only the ones the window actually covers.
  const startOf = (s: T) => {
    const raw = timeToMinutes(s.start || '');
    return raw < wakeMin && raw + DAY <= bedMin ? raw + DAY : raw;
  };

  const ordered = slots
    .map((slot, i) => ({ slot, i, at: startOf(slot) }))
    // The index breaks ties so two slots at the same minute keep the order the
    // model emitted them in rather than an arbitrary one.
    .sort((a, b) => a.at - b.at || a.i - b.i);

  const lengthOf = (s: T) => (typeof s.durationMin === 'number' ? s.durationMin : 0);
  const isFixed = (s: T) => !!(s.journey || s.locked) || !s.start;

  // The cursor alone only knows what came before, so it cannot see a pinned
  // 09:15 slot while placing an 08:00 one. These are the times already spoken
  // for, in both directions.
  const reserved = ordered
    .filter(o => isFixed(o.slot))
    .map(o => ({ from: o.at, to: o.at + lengthOf(o.slot) }));

  /** The first start at or after `from` where `duration` clears every reservation. */
  const clearOf = (from: number, duration: number) => {
    let at = from;
    for (let pass = 0; pass <= reserved.length; pass++) {
      const hit = reserved.find(r => at < r.to && r.from < at + duration);
      if (!hit) break;
      at = hit.to;
    }
    return at;
  };

  let cursor = wakeMin;
  const moved = ordered.map(({ slot, at }) => {
    const duration = lengthOf(slot);

    if (isFixed(slot)) {
      cursor = Math.max(cursor, at + duration);
      return { slot, at };
    }

    // Never before the previous slot ends; never so late that it would run past
    // bedtime, unless the day is already so full that there is nowhere earlier.
    const latest = Math.max(cursor, bedMin - duration);
    const next = clearOf(Math.min(Math.max(at, cursor), latest), duration);
    cursor = next + duration;
    return { slot: { ...slot, start: toHHMM(next) }, at: next };
  });

  return moved.sort((a, b) => a.at - b.at).map(m => m.slot as T);
}
